import { useState, type FormEvent } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { LockKeyhole, LogOut, Menu, ShieldCheck, X } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { getDisplayName, validatePasswordChangeInput } from '../lib/auth';
import type { AdminSurfaceConfig } from '../surface';

interface AdminLayoutProps {
  surfaceConfig: AdminSurfaceConfig;
}

export default function AdminLayout({ surfaceConfig }: AdminLayoutProps) {
  const { status, user, profile, signOut } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const location = useLocation();

  if (status === 'config_error' || status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (status === 'loading') {
    return (
      <div className="admin-loading">
        <div className="spinner" />
      </div>
    );
  }

  const displayName = getDisplayName(profile, user);

  return (
    <div className="admin-shell">
      <button className="mobile-menu-button" type="button" onClick={() => setIsDrawerOpen(true)}>
        <Menu size={20} />
        <span className="sr-only">메뉴 열기</span>
      </button>

      {isDrawerOpen ? (
        <button className="drawer-backdrop" type="button" onClick={() => setIsDrawerOpen(false)} />
      ) : null}

      <aside className={`sidebar ${isDrawerOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <Link to="/" className="brand-link" onClick={() => setIsDrawerOpen(false)}>
            <span className="brand-mark">
              <ShieldCheck size={21} />
            </span>
            <span>
              <strong>{surfaceConfig.title}</strong>
              <small>{surfaceConfig.shortTitle}</small>
            </span>
          </Link>
          <button className="icon-button sidebar__close" type="button" onClick={() => setIsDrawerOpen(false)}>
            <X size={18} />
            <span className="sr-only">메뉴 닫기</span>
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="관리자 메뉴">
          {surfaceConfig.navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}
                onClick={() => setIsDrawerOpen(false)}
              >
                <Icon size={18} />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar__footer">
          <div className="operator">
            <span className="operator__badge">{surfaceConfig.badge}</span>
            <span>
              <strong>{displayName}</strong>
              <small>{profile?.role}</small>
            </span>
          </div>
          <button className="account-button" type="button" onClick={() => setIsPasswordDialogOpen(true)}>
            <LockKeyhole size={17} />
            비밀번호 변경
          </button>
          <button className="logout-button" type="button" onClick={() => void signOut()}>
            <LogOut size={17} />
            로그아웃
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="topbar">
          <div>
            <p className="topbar__eyebrow">{surfaceConfig.badge} Console</p>
            <h1>{surfaceConfig.title}</h1>
          </div>
          <Link className="home-shortcut" to="/">
            홈으로
          </Link>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
      {isPasswordDialogOpen ? <PasswordChangeDialog onClose={() => setIsPasswordDialogOpen(false)} /> : null}
    </div>
  );
}

interface PasswordChangeDialogProps {
  onClose: () => void;
}

function PasswordChangeDialog({ onClose }: PasswordChangeDialogProps) {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    const validationError = validatePasswordChangeInput({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMessage('비밀번호가 변경되었습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '비밀번호를 변경하지 못했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-panel modal-panel--narrow" aria-labelledby="password-change-title">
        <div className="modal-heading">
          <div>
            <h3 id="password-change-title">비밀번호 변경</h3>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="닫기">
            <X size={18} />
          </button>
        </div>

        <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
          {errorMessage ? <div className="alert alert--error">{errorMessage}</div> : null}
          {successMessage ? <div className="alert alert--success">{successMessage}</div> : null}

          <label className="form-field">
            현재 비밀번호
            <input
              type="password"
              value={currentPassword}
              autoComplete="current-password"
              onChange={(event) => setCurrentPassword(event.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <label className="form-field">
            새 비밀번호
            <input
              type="password"
              value={newPassword}
              autoComplete="new-password"
              onChange={(event) => setNewPassword(event.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <label className="form-field">
            새 비밀번호 확인
            <input
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <div className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose} disabled={isSubmitting}>
              닫기
            </button>
            <button
              className="primary-button"
              type="submit"
              disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
            >
              {isSubmitting ? '변경 중' : '변경'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
